import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  useGetInvoicesQuery,
  useDeleteInvoiceMutation,
} from "@/features/api/invoiceApi";

const InvoiceList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("invoiceDate");
  const [sortOrder, setSortOrder] = useState("desc");
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  const navigate = useNavigate();

  // Build API params
  const queryArgs = useMemo(
    () => ({
      search: searchQuery.trim() || undefined,
      sortBy,
      sortOrder,
      from: startDate ? startDate.toISOString() : undefined,
      to: endDate ? endDate.toISOString() : undefined,
    }),
    [searchQuery, sortBy, sortOrder, startDate, endDate]
  );

  // Fetch invoices
  const { data, isLoading, isFetching, isError, error } = useGetInvoicesQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  const [deleteInvoice, { isLoading: isDeleting }] = useDeleteInvoiceMutation();

  // Normalize API data
  const invoices = useMemo(
    () => (Array.isArray(data?.invoices) ? data.invoices : []),
    [data]
  );

  // Sort invoices
  const sortedInvoices = useMemo(() => {
    return [...invoices].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "invoiceNo":
          aVal = a.invoiceNo ?? "";
          bVal = b.invoiceNo ?? "";
          break;
        case "clientName":
          aVal = a.billTo?.clientName ?? "";
          bVal = b.billTo?.clientName ?? "";
          break;
        case "companyName":
          aVal = a.billTo?.companyName ?? "";
          bVal = b.billTo?.companyName ?? "";
          break;
        case "invoiceDate":
          aVal = new Date(a.invoiceDate ?? 0).getTime();
          bVal = new Date(b.invoiceDate ?? 0).getTime();
          break;
        case "total":
          aVal = a.total ?? 0;
          bVal = b.total ?? 0;
          break;
        default:
          aVal = a.invoiceDate ? new Date(a.invoiceDate).getTime() : 0;
          bVal = b.invoiceDate ? new Date(b.invoiceDate).getTime() : 0;
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [invoices, sortBy, sortOrder]);

  // Toggle sorting
  const toggleSortOrder = useCallback(
    (field) => {
      setSortBy(field);
      setSortOrder((prev) => (prev === "asc" && field === sortBy ? "desc" : "asc"));
    },
    [sortBy]
  );

  // Delete handler
  const handleDelete = async (invoiceNo) => {
    if (!invoiceNo) return;
    if (window.confirm("Are you sure you want to delete this invoice?")) {
      try {
        await deleteInvoice(invoiceNo).unwrap();
        toast.success("Invoice deleted successfully");
      } catch (err) {
        toast.error(err?.data?.message || "Failed to delete invoice");
      }
    }
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return isNaN(d) ? "N/A" : d.toLocaleDateString("en-US");
  };

  // Format currency
  const formatCurrency = (amount = 0) =>
    new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount);

  // Error handling
  useEffect(() => {
    if (isError) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
      } else {
        toast.error(error?.data?.message || "Failed to fetch invoices");
      }
    }
  }, [isError, error, navigate]);

  return (
    <div className="flex-1 p-6 bg-background min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl font-semibold">🧾 All Invoices</h2>
        <Button
          onClick={() => navigate("/admin/invoices/create")}
          aria-label="Create new invoice"
        >
          Create New Invoice
        </Button>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
        <Input
          type="text"
          placeholder="Search by Invoice No, Client, or Company"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="max-w-md"
          aria-label="Search invoices"
        />
        <div className="flex gap-2 items-center">
          <DatePicker
            selected={startDate}
            onChange={(update) => setDateRange(update)}
            startDate={startDate}
            endDate={endDate}
            selectsRange
            placeholderText="Filter by Date Range"
            className="border rounded px-2 py-1 text-sm"
            aria-label="Filter by date range"
          />
          {(startDate || endDate) && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setDateRange([null, null])}
              aria-label="Clear date range filter"
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {isError ? (
        <div className="text-center text-red-500">
          {error?.data?.message || "Failed to fetch invoices"}
        </div>
      ) : isLoading || isFetching ? (
        <div className="text-center flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading invoices...
        </div>
      ) : sortedInvoices.length === 0 ? (
        <div className="text-center text-gray-500">📭 No invoices found</div>
      ) : (
        <Table>
          <TableCaption>List of all saved invoices</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead
                onClick={() => toggleSortOrder("invoiceNo")}
                className="cursor-pointer"
              >
                Invoice No {sortBy === "invoiceNo" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("clientName")}
                className="cursor-pointer"
              >
                Client {sortBy === "clientName" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("companyName")}
                className="cursor-pointer"
              >
                Company {sortBy === "companyName" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("invoiceDate")}
                className="cursor-pointer"
              >
                Date {sortBy === "invoiceDate" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("total")}
                className="cursor-pointer"
              >
                Total {sortBy === "total" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedInvoices.map((invoice, index) => (
              <TableRow
                key={invoice.invoiceNo}
                onClick={() => navigate(`/admin/invoices/${invoice.invoiceNo}`)}
                className="hover:bg-accent/30 cursor-pointer transition"
              >
                <TableCell className="font-medium">{invoice.invoiceNo}</TableCell>
                <TableCell>{invoice.billTo?.clientName || "N/A"}</TableCell>
                <TableCell>{invoice.billTo?.companyName || "N/A"}</TableCell>
                <TableCell>{formatDate(invoice.invoiceDate)}</TableCell>
                <TableCell>{formatCurrency(invoice.total)}</TableCell>
                <TableCell
                  className="text-right flex gap-2 justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/admin/invoices/${invoice.invoiceNo}/edit`)}
                    title="Edit"
                    aria-label={`Edit invoice ${invoice.invoiceNo}`}
                  >
                    <Edit size={18} />
                  </Button>
                  <Button
                    variant="destructive"
                    size="icon"
                    onClick={() => handleDelete(invoice.invoiceNo)}
                    title="Delete"
                    disabled={isDeleting}
                    aria-label={`Delete invoice ${invoice.invoiceNo}`}
                  >
                    <Trash2 size={18} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default InvoiceList;